import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { ROUTES } from "../../utils/constants";
import { useTheme } from "../../contexts/ThemeContext";
import {
  Shield,
  Lock,
  Eye,
  UserCheck,
  Database,
  Cookie,
  Globe,
  Mail,
  AlertTriangle,
  Moon,
  Sun,
} from "lucide-react";

export const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-app-bg dark:bg-gray-950 text-app-text dark:text-gray-100">
      {/* ✅ Header (Landing-style) */}
      <header className="sticky top-0 z-50 border-b border-app-border/40 dark:border-gray-800 bg-app-surface/70 dark:bg-gray-900/70 backdrop-blur-md">
        <div className="mx-auto grid max-w-6xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 h-[72px] sm:px-6 lg:px-8">
          {/* LEFT: Logo */}
          <Link
            to={ROUTES.LANDING}
            className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors"
            aria-label="TKM Home"
          >
            <span
              className="
                relative flex h-12 w-12 items-center justify-center
                rounded-2xl overflow-hidden
                bg-white ring-1 ring-black/10
                shadow-[0_10px_24px_rgba(0,0,0,0.18)]
              "
            >
              <img
                src="/images/tkm-logo.png"
                alt="TKM Logo"
                className="h-full w-full object-contain p-1"
                draggable={false}
              />
            </span>

            <div className="hidden md:block leading-tight text-left">
              <div className="text-base font-extrabold text-app-text dark:text-white">
                Tamil Katral Maiyam
              </div>
              <div className="text-xs font-bold text-app-text-muted dark:text-gray-400">
                Privacy & Safety
              </div>
            </div>
          </Link>

          {/* CENTER: Page title (mobile) */}
          <div className="md:hidden text-center">
            <div className="text-sm font-extrabold text-app-text dark:text-white">
              Privacy & Safety
            </div>
          </div>

          {/* RIGHT: Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            <Button
              variant="outline"
              size="sm"
              className="border-app-border dark:border-gray-700"
              onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
            >
              Log in
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-14 sm:py-16">
        <div className="flex items-start gap-4">
          <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-3">
            <Shield className="h-10 w-10 text-primary-600 dark:text-primary-400" />
          </div>

          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-app-text dark:text-white">
              Privacy & Safety
            </h1>
            <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400">
              Last updated: February 2026
            </p>
          </div>
        </div>

        <p className="mt-6 text-app-text-muted dark:text-gray-400 leading-relaxed">
          TKM Learning Platform is designed to support child-safe learning experiences.
          We aim to follow applicable privacy principles (including COPPA-style child
          protections and GDPR-style data rights). We do not sell personal data. Schools
          and parents retain control over learner information.
        </p>

        {/* Key Principles */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">
            Our privacy principles
          </h2>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <p className="font-bold">Security-first</p>
              </div>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                We use industry-standard safeguards to protect accounts, content, and
                platform access.
              </p>
            </div>

            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <p className="font-bold">Transparent usage</p>
              </div>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                We clearly explain what data is collected and why, so schools and
                parents can make informed decisions.
              </p>
            </div>

            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <div className="flex items-center gap-3">
                <UserCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <p className="font-bold">School & parent control</p>
              </div>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                Schools manage student access. Parents and schools can request updates,
                corrections, or deletion where applicable.
              </p>
            </div>

            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <div className="flex items-center gap-3">
                <Database className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <p className="font-bold">Data minimization</p>
              </div>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                We collect only what we need to deliver learning features and improve
                product quality.
              </p>
            </div>
          </div>
        </section>

        {/* What we collect */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">
            What we collect
          </h2>

          <div className="mt-4 space-y-4">
            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <p className="font-bold">Account & roster details</p>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                Student/teacher identifiers provided by the school (e.g., name, class,
                school ID) and basic login/session details.
              </p>
            </div>

            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <p className="font-bold">Learning activity</p>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                Progress, completion, attempts, and skill mastery signals (e.g., Can-Do
                tracking, quiz scores, practice usage).
              </p>
            </div>

            <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
              <p className="font-bold">Voice & writing inputs (when enabled)</p>
              <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                If a school enables AI conversation or writing tools, we may process
                student inputs to provide feedback. Schools control whether these
                features are available.
              </p>
            </div>
          </div>
        </section>

        {/* Cookies */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">
            Cookies & similar technologies
          </h2>

          <div className="mt-4 rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
            <div className="flex items-center gap-3">
              <Cookie className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <p className="font-bold">Session & performance cookies</p>
            </div>
            <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
              We use cookies/local storage primarily to keep users signed in, maintain
              preferences (like theme), and support basic performance monitoring.
            </p>
          </div>
        </section>

        {/* Rights */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">
            Your choices & rights
          </h2>

          <div className="mt-4 rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
            <ul className="space-y-3 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="mt-[6px] h-2 w-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                Request access, correction, or deletion of learner data through the
                school administrator.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[6px] h-2 w-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                Schools can disable optional AI/voice features at any time.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[6px] h-2 w-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                Parents can ask schools for a summary of learning data and progress.
              </li>
            </ul>
          </div>
        </section>

        {/* Safety */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">
            Child safety & content safeguards
          </h2>

          <div className="mt-4 rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <p className="font-bold">Safe-by-design</p>
            </div>
            <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
              We focus on education-first interactions, classroom-friendly UI/UX, and
              role-based access controls. Schools can manage accounts, roles, and feature
              availability.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-app-text dark:text-white">Contact</h2>

          <div className="mt-4 rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/70 p-5">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <p className="font-bold">Need help?</p>
            </div>
            <p className="mt-2 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
              For questions, contact your school administrator or reach out to our
              support team.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate(ROUTES.LANDING)}>
                Back to home
              </Button>
              <Button onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}>
                Log in
              </Button>
            </div>
          </div>
        </section>

        <div className="mt-10 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(ROUTES.LANDING)}>
            <span className="inline-flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Back to home
            </span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
          >
            Log in
          </Button>
        </div>
      </main>
    </div>
  );
};
